import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MotionConfig, motion } from "framer-motion";
import Logo from "../image/kigalianimalfeeds.png";

const Navbar = () => {
  const [nav, setNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const handleNav = () => {
    setNav(!nav);
  };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setNav(false);
  }, [location]);

  // Array containing navigation items
  const navItems = [
    { id: "home", text: "Home", to: "/" },
    { id: "services", text: "Services", to: "/services" },
    { id: "about", text: "About Us", to: "/about" },
    { id: "contact", text: "Contact Us", to: "/contact" },
  ];

  // Check if current route matches nav item
  const isActive = (path) => location.pathname === path;

  return (
    <div className="relative">
      {/* Navbar */}
      <motion.div
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`flex justify-between items-center h-20 md:h-24 w-full px-4 md:px-6 lg:px-10 text-primary fixed top-0 left-0 z-50 transition-all duration-300 ${
          scrolled
            ? "glass-white shadow-lg backdrop-blur-md"
            : "bg-white shadow-md"
        }`}
      >
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <motion.img
            src={Logo}
            alt="Kigali Animal Feeds Logo"
            className="h-16 md:h-20 transition-transform duration-300 group-hover:scale-105"
            whileHover={{ rotate: [0, -5, 5, 0] }}
            transition={{ duration: 0.5 }}
          />
        </Link>

        {/* Desktop Navigation */}
        <ul className="hidden md:flex items-center gap-2">
          {navItems.map((item) => (
            <li key={item.id}>
              <Link
                to={item.to}
                className={`relative px-4 py-2 font-medium transition-all duration-300 rounded-lg group ${
                  isActive(item.to)
                    ? "text-primary"
                    : "text-gray-700 hover:text-primary"
                }`}
              >
                <span className="relative z-10">{item.text}</span>
                
                {/* Hover background effect */}
                <span className="absolute inset-0 bg-primary/5 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300"></span>
                
                {/* Active/Hover underline */}
                <span
                  className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-primary transition-all duration-300 ${
                    isActive(item.to)
                      ? "w-3/4"
                      : "w-0 group-hover:w-3/4"
                  }`}
                ></span>
              </Link>
            </li>
          ))}
        </ul>

        {/* Mobile Navigation Icon (Animated Hamburger Button) */}
        <div onClick={handleNav} className="block md:hidden z-50">
          <MotionConfig
            transition={{
              duration: 0.5,
              ease: "easeInOut",
            }}
          >
            <motion.button
              initial={false}
              animate={nav ? "open" : "closed"}
              className="relative h-12 w-12 rounded-full bg-primary/10 flex justify-center items-center hover:bg-primary/20 transition-colors"
            >
              {/* Top Bar */}
              <motion.span
                variants={VARIANTS.top}
                className="absolute h-0.5 w-6 bg-primary rounded-full"
              />
              {/* Middle Bar */}
              <motion.span
                variants={VARIANTS.middle}
                className="absolute h-0.5 w-6 bg-primary rounded-full"
              />
              {/* Bottom Bar */}
              <motion.span
                variants={VARIANTS.bottom}
                className="absolute h-0.5 w-6 bg-primary rounded-full"
              />
            </motion.button>
          </MotionConfig>
        </div>
      </motion.div>

      {/* Mobile Navigation Menu */}
      <motion.div
        initial={{ opacity: 0, x: "100%" }}
        animate={{
          opacity: nav ? 1 : 0,
          x: nav ? 0 : "100%",
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={`fixed right-0 top-20 md:top-24 w-full sm:w-80 h-[calc(100vh-5rem)] md:h-[calc(100vh-6rem)] bg-white shadow-2xl z-40 ${
          nav ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <ul className="flex flex-col p-6 space-y-2">
          {navItems.map((item, index) => (
            <motion.li
              key={item.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{
                opacity: nav ? 1 : 0,
                x: nav ? 0 : 50,
              }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
            >
              <Link
                to={item.to}
                onClick={handleNav}
                className={`block px-6 py-4 text-lg font-medium rounded-xl transition-all duration-300 ${
                  isActive(item.to)
                    ? "bg-gradient-primary text-white shadow-primary"
                    : "text-gray-700 hover:bg-primary/5 hover:text-primary"
                }`}
              >
                {item.text}
              </Link>
            </motion.li>
          ))}
        </ul>

        {/* Mobile Menu Footer */}
        <div className="absolute bottom-6 left-6 right-6 p-4 bg-gradient-primary rounded-xl text-white text-center">
          <p className="text-sm font-medium">
            Transform Farming with Quality Feeds
          </p>
        </div>
      </motion.div>

      {/* Backdrop for mobile menu */}
      {nav && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleNav}
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 md:hidden"
        />
      )}
    </div>
  );
};

// Framer Motion Animation Variants
const VARIANTS = {
  top: {
    open: {
      rotate: 45,
      top: "50%",
    },
    closed: {
      rotate: 0,
      top: "35%",
    },
  },
  middle: {
    open: {
      opacity: 0,
    },
    closed: {
      opacity: 1,
    },
  },
  bottom: {
    open: {
      rotate: -45,
      bottom: "50%",
    },
    closed: {
      rotate: 0,
      bottom: "35%",
    },
  },
};

export default Navbar;
